import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpMethod } from '@vision/ui/enums';
import type {
  Application,
  ApplicationResponseDto,
  CreateApplicationArgs,
  UpdateApplicationArgs,
} from './applications.types';
import { applicationFromApi, applicationToApi } from './applications.mappers';
import { createHttpExtraOptions } from '@vision/ui/utils';
import { axiosBaseQuery } from './base.service';

export const ApplicationsService = createApi({
  reducerPath: 'ApplicationsService',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Applications'],
  endpoints: (builder) => ({
    getApplications: builder.query<Application[], void>({
      query: () => ({
        extraOptions: createHttpExtraOptions({
          notification: { translationMessageKey: 'http.message.application.getApplications.{{status}}' },
        }),
        url: '/v1/platform_applications',
        method: HttpMethod.GET,
      }),
      transformResponse: (response: ApplicationResponseDto[]) => response.map(applicationFromApi),
      providesTags: ['Applications'],
    }),
    createApplication: builder.mutation<Application, CreateApplicationArgs>({
      query: (data) => ({
        url: '/v1/platform_applications',
        method: HttpMethod.POST,
        data: applicationToApi(data),
        extraOptions: createHttpExtraOptions({
          notification: {
            translationMessageKey: 'http.message.application.createApplication.{{status}}',
          },
        }),
      }),
      transformResponse: applicationFromApi,
      invalidatesTags: (_result, error) => (error ? [] : ['Applications']),
    }),
    updateApplication: builder.mutation<Application, { id: string; data: UpdateApplicationArgs }>({
      query: ({ id, data }) => ({
        url: `/v1/platform_applications/${id}/update`,
        method: HttpMethod.POST,
        data: applicationToApi(data),
        extraOptions: createHttpExtraOptions({
          notification: {
            translationMessageKey: 'http.message.application.updateApplication.{{status}}',
          },
        }),
      }),
      transformResponse: applicationFromApi,
      invalidatesTags: (_result, error) => (error ? [] : ['Applications']),
    }),
    deleteApplication: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/v1/platform_applications/${id}/destroy`,
        method: HttpMethod.POST,
        extraOptions: createHttpExtraOptions({
          notification: { translationMessageKey: 'http.message.application.deleteApplication.{{status}}' },
        }),
      }),
      transformResponse: () => undefined,
      invalidatesTags: (_result, error) => (error ? [] : ['Applications']),
    }),
  }),
});

export const {
  useGetApplicationsQuery,
  useCreateApplicationMutation,
  useUpdateApplicationMutation,
  useDeleteApplicationMutation,
} = ApplicationsService;
